import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  CheckCircle,
  Lock,
  MessageSquare,
  Plus,
  RefreshCw,
  Send,
  User,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { communityService } from '../../services/communityService';
import type { CommunityComment, EnrolledSubject } from '../../types/community';
import { cardTheme } from '../../styles/cardTheme';
import {
  formatCommentDate,
  getCommentStatusLabel,
  getCommentStatusStyle,
  getReplyDisplayName,
} from '../../utils/communityLabels';

const PAGE_SIZE = 10;

type Tab = 'feed' | 'mine';

function CommentCard({
  comment,
  showStatus,
}: {
  comment: CommunityComment;
  showStatus?: boolean;
}) {
  const contextLabel = [comment.subjectName, comment.courseName, comment.unitName, comment.lessonName]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className={`${cardTheme.shell} ${cardTheme.accentTop} p-4 sm:p-5 space-y-3`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${cardTheme.iconBox}`}
          >
            <User size={16} />
          </div>
          <div className="min-w-0">
            <p className={`truncate text-sm font-semibold font-cairo-semibold ${cardTheme.title}`}>
              {comment.studentName}
            </p>
            <p className="text-xs text-gray-500">{formatCommentDate(comment.createdAt)}</p>
          </div>
        </div>
        {showStatus && (
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${getCommentStatusStyle(comment.status)}`}
          >
            {getCommentStatusLabel(comment.status)}
          </span>
        )}
      </div>

      {contextLabel && (
        <p className={`${cardTheme.badge} max-w-full truncate`}>{contextLabel}</p>
      )}

      <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{comment.text}</p>

      {comment.replies.length > 0 && (
        <div className="space-y-2 border-t border-green-100 pt-3">
          <p className="text-xs font-semibold text-gray-500">الردود ({comment.replies.length})</p>
          {comment.replies.map((reply) => (
            <div
              key={reply.id}
              className="space-y-1 rounded-xl border border-green-50 bg-green-50/50 p-3 text-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`font-medium font-cairo-semibold ${cardTheme.title}`}>
                  {getReplyDisplayName(reply)}
                </span>
                <span className="shrink-0 text-xs text-gray-500">
                  {formatCommentDate(reply.createdAt)}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-gray-600">{reply.text}</p>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

const PortalForum: React.FC = () => {
  const { currentUser, hasEnrolledCourses, checkEnrolledCourses } = useAuth();
  const isCheckingEnrollment = hasEnrolledCourses === null;
  const [tab, setTab] = useState<Tab>('feed');
  const [subjects, setSubjects] = useState<EnrolledSubject[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState('');
  const [comments, setComments] = useState<CommunityComment[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCompose, setShowCompose] = useState(false);
  const [newText, setNewText] = useState('');
  const [composeSubjectId, setComposeSubjectId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const fetchSubjects = useCallback(async () => {
    setSubjectsLoading(true);
    setSubjectsError('');
    const result = await communityService.getEnrolledSubjects();
    if (result.success) {
      setSubjects(result.data);
      setSelectedSubjectId((prev) => {
        if (prev && result.data.some((s) => s.id === prev)) return prev;
        return result.data[0]?.id ?? '';
      });
      if (result.data.length === 0) {
        setComments([]);
        setLoading(false);
      }
    } else {
      setSubjects([]);
      setSelectedSubjectId('');
      setSubjectsError(result.error);
      setComments([]);
      setLoading(false);
    }
    setSubjectsLoading(false);
  }, []);

  const fetchComments = useCallback(
    async (targetTab: Tab, targetPage: number, subjectId: string) => {
      if (!subjectId) {
        setComments([]);
        setTotalPages(1);
        setPage(1);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      const params = { subjectId, page: targetPage, pageSize: PAGE_SIZE };
      const result =
        targetTab === 'feed'
          ? await communityService.getPublishedComments(params)
          : await communityService.getMyComments(params);

      if (result.success) {
        setComments(result.data.comments);
        setTotalPages(Math.max(result.data.totalPages, 1));
        setPage(result.data.page);
      } else {
        setComments([]);
        setError(result.error);
      }
      setLoading(false);
    },
    []
  );

  useEffect(() => {
    if (currentUser?.id && hasEnrolledCourses === null) {
      checkEnrolledCourses();
    }
  }, [currentUser?.id, hasEnrolledCourses, checkEnrolledCourses]);

  useEffect(() => {
    if (!hasEnrolledCourses) return;
    fetchSubjects();
  }, [hasEnrolledCourses, fetchSubjects]);

  useEffect(() => {
    if (!hasEnrolledCourses || !selectedSubjectId) return;
    fetchComments(tab, 1, selectedSubjectId);
  }, [tab, selectedSubjectId, fetchComments, hasEnrolledCourses]);

  useEffect(() => {
    if (!showCompose && !showSuccessPopup) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowCompose(false);
        setShowSuccessPopup(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showCompose, showSuccessPopup]);

  const openCompose = () => {
    setSubmitError('');
    setComposeSubjectId(selectedSubjectId || subjects[0]?.id || '');
    setShowCompose(true);
  };

  const closeCompose = () => {
    if (submitting) return;
    setShowCompose(false);
    setSubmitError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newText.trim();
    if (!text) {
      setSubmitError('اكتب سؤالك أولاً');
      return;
    }
    if (!composeSubjectId) {
      setSubmitError('اختر المادة أولاً');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    const result = await communityService.createComment({
      text,
      subjectId: composeSubjectId,
      courseId: null,
      grade: currentUser?.grade || null,
    });

    setSubmitting(false);

    if (result.success) {
      setNewText('');
      setShowCompose(false);
      setShowSuccessPopup(true);
      if (composeSubjectId !== selectedSubjectId) {
        setSelectedSubjectId(composeSubjectId);
      }
      if (tab === 'mine') {
        fetchComments('mine', 1, composeSubjectId);
      }
    } else {
      setSubmitError(result.error);
    }
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages || !selectedSubjectId) return;
    fetchComments(tab, nextPage, selectedSubjectId);
  };

  const handleSubjectChange = (subjectId: string) => {
    if (subjectId === selectedSubjectId) return;
    setSelectedSubjectId(subjectId);
  };

  if (isCheckingEnrollment) {
    return (
      <div className="mx-auto max-w-2xl p-4 font-cairo sm:p-6 lg:p-8" dir="rtl">
        <div className="mb-6 sm:mb-8">
          <span className={`mb-3 inline-flex ${cardTheme.badge}`}>
            <MessageSquare className="h-4 w-4" />
            المنتدى
          </span>
          <h1 className={`text-2xl font-bold sm:text-3xl ${cardTheme.title}`}>منتدى الطلاب</h1>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`${cardTheme.shell} h-28 animate-pulse`} />
          ))}
        </div>
      </div>
    );
  }

  if (!hasEnrolledCourses) {
    return (
      <div className="mx-auto max-w-2xl p-4 font-cairo sm:p-6 lg:p-8" dir="rtl">
        <div className="mb-6 sm:mb-8">
          <span className={`mb-3 inline-flex ${cardTheme.badge}`}>
            <MessageSquare className="h-4 w-4" />
            المنتدى
          </span>
          <h1 className={`mb-2 text-2xl font-bold sm:text-3xl ${cardTheme.title}`}>منتدى الطلاب</h1>
        </div>

        <div className={`${cardTheme.shell} p-8 text-center sm:p-10`}>
          <div
            className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl ${cardTheme.iconBoxGold}`}
          >
            <Lock className="h-8 w-8" />
          </div>
          <h2 className={`mb-3 text-xl font-bold ${cardTheme.title}`}>المنتدى لسه مقفول عليك</h2>
          <p className={`mx-auto mb-2 max-w-sm text-sm leading-relaxed ${cardTheme.subtitle}`}>
            هتقدر تشوف المنتدى وتشارك فيه بعد ما تشترك في أي كورس
          </p>
          <p className="mx-auto mb-6 max-w-sm text-sm leading-relaxed text-gray-500">
            اشترك في كورس دلوقتي وانضم لزملائك في النقاش
          </p>
          <Link
            to="/student-portal/browse"
            className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold ${cardTheme.btnPrimary}`}
          >
            <BookOpen size={16} />
            تصفح الكورسات
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-2xl p-4 pb-28 font-cairo sm:p-6 sm:pb-28 lg:p-8" dir="rtl">
      {/* Success popup */}
      {showSuccessPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          dir="rtl"
          onClick={() => setShowSuccessPopup(false)}
          role="presentation"
        >
          <div
            className={`${cardTheme.shell} relative mx-4 w-full max-w-sm p-6 shadow-xl`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="forum-success-title"
          >
            <button
              type="button"
              onClick={() => setShowSuccessPopup(false)}
              className="absolute left-3 top-3 flex min-h-11 min-w-11 items-center justify-center rounded-xl text-gray-400 hover:bg-green-50 hover:text-green-800"
              aria-label="إغلاق"
            >
              <X size={18} />
            </button>
            <div className="pt-2 text-center">
              <div
                className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${cardTheme.iconBox}`}
              >
                <CheckCircle className="h-8 w-8" />
              </div>
              <h3 id="forum-success-title" className={`mb-2 text-xl font-bold ${cardTheme.title}`}>
                تمام كده!
              </h3>
              <p className={`mb-1 text-sm leading-relaxed ${cardTheme.subtitle}`}>
                سؤالك اتبعت بنجاح
              </p>
              <p className="text-sm leading-relaxed text-gray-500">
                هيتراجع الأول وبعدين هينزل في المنتدى إن شاء الله
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowSuccessPopup(false)}
              className={`mt-6 flex w-full min-h-12 items-center justify-center rounded-xl px-4 text-sm font-semibold ${cardTheme.btnPrimary}`}
            >
              تمام
            </button>
          </div>
        </div>
      )}

      {/* Compose popup */}
      {showCompose && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          dir="rtl"
          onClick={closeCompose}
          role="presentation"
        >
          <div
            className={`${cardTheme.shell} w-full max-w-lg rounded-b-none sm:rounded-2xl`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="forum-compose-title"
          >
            <div className={`${cardTheme.headerBanner} px-4 py-4 sm:px-5`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <span
                    className={`mb-2 inline-flex ${cardTheme.badgeGold} border-gold-400/40 bg-gold-500/20 text-gold-200`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    سؤال جديد
                  </span>
                  <h2
                    id="forum-compose-title"
                    className="truncate text-base font-bold text-white sm:text-lg"
                  >
                    اكتب سؤالك
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closeCompose}
                  disabled={submitting}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20 disabled:opacity-50"
                  aria-label="إغلاق"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 p-4 sm:p-5">
              {subjects.length > 0 && (
                <div className="space-y-1.5">
                  <label
                    htmlFor="compose-subject"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    المادة
                  </label>
                  <select
                    id="compose-subject"
                    value={composeSubjectId}
                    onChange={(e) => setComposeSubjectId(e.target.value)}
                    disabled={submitting}
                    className="min-h-11 w-full rounded-xl border-2 border-green-100 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition focus:border-gold-400"
                  >
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                        {subject.gradeName ? ` · ${subject.gradeName}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                rows={5}
                autoFocus
                placeholder="اكتب سؤالك أو تعليقك هنا..."
                className="w-full resize-none rounded-xl border-2 border-green-100 bg-white px-3.5 py-3 text-sm leading-relaxed text-gray-900 outline-none transition focus:border-gold-400 sm:text-base"
              />

              {submitError && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {submitError}
                </p>
              )}

              <div className="flex flex-col gap-2 sm:flex-row-reverse sm:justify-start">
                <button
                  type="submit"
                  disabled={submitting || !composeSubjectId}
                  className={`inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold disabled:opacity-60 sm:w-auto sm:min-w-[9rem] ${cardTheme.btnPrimary}`}
                >
                  <Send size={16} />
                  {submitting ? 'جاري الإرسال...' : 'إرسال السؤال'}
                </button>
                <button
                  type="button"
                  onClick={closeCompose}
                  disabled={submitting}
                  className={`inline-flex min-h-12 w-full items-center justify-center rounded-xl px-5 text-sm font-bold disabled:opacity-60 sm:w-auto ${cardTheme.btnOutline}`}
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={`${cardTheme.shell} mb-5 overflow-hidden sm:mb-6`}>
        <div className={`${cardTheme.headerBanner} px-4 py-5 sm:px-6 sm:py-6`}>
          <span
            className={`mb-3 inline-flex ${cardTheme.badgeGold} border-gold-400/40 bg-gold-500/20 text-gold-200`}
          >
            <MessageSquare className="h-3.5 w-3.5" />
            المنتدى
          </span>
          <h1 className="text-xl font-bold text-white sm:text-2xl">منتدى الطلاب</h1>
          <p className="mt-1 text-sm text-green-100/90 sm:text-base">
            شارك أسئلتك واطّلع على تعليقات زملائك حسب المادة
          </p>
        </div>
      </div>

      {/* Subject filter */}
      <div className="mb-4">
        <p className="mb-2 text-xs font-semibold text-gray-500 sm:text-sm">المواد المسجّلة</p>
        {subjectsLoading ? (
          <div className="flex gap-2 overflow-hidden">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-11 w-24 shrink-0 animate-pulse rounded-xl bg-green-50" />
            ))}
          </div>
        ) : subjectsError ? (
          <div className={`${cardTheme.shell} border-red-200 p-4 text-center text-sm text-red-600`}>
            {subjectsError}
            <button
              type="button"
              onClick={fetchSubjects}
              className={`mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold ${cardTheme.btnOutline}`}
            >
              <RefreshCw size={14} />
              إعادة المحاولة
            </button>
          </div>
        ) : subjects.length === 0 ? (
          <div className={`${cardTheme.shell} p-6 text-center`}>
            <p className={`mb-1 text-sm font-bold ${cardTheme.title}`}>لا توجد مواد مسجّلة</p>
            <p className={`text-sm ${cardTheme.subtitle}`}>اشترك في كورس لعرض تعليقات مواده</p>
          </div>
        ) : (
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:thin]">
            {subjects.map((subject) => {
              const active = subject.id === selectedSubjectId;
              return (
                <button
                  key={subject.id}
                  type="button"
                  onClick={() => handleSubjectChange(subject.id)}
                  className={`min-h-11 shrink-0 rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors sm:px-4 ${
                    active
                      ? 'bg-green-800 text-white shadow-sm'
                      : 'border border-green-100 bg-white text-green-800 hover:bg-green-50'
                  }`}
                >
                  {subject.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-1 border-b border-green-100">
        <button
          type="button"
          onClick={() => setTab('feed')}
          className={`min-h-11 px-3 py-2.5 text-sm font-semibold font-cairo-semibold border-b-2 transition-colors sm:px-4 ${
            tab === 'feed'
              ? 'border-green-700 text-green-800'
              : 'border-transparent text-gray-500 hover:text-green-700'
          }`}
        >
          تعليقات المجتمع
        </button>
        <button
          type="button"
          onClick={() => setTab('mine')}
          className={`min-h-11 px-3 py-2.5 text-sm font-semibold font-cairo-semibold border-b-2 transition-colors sm:px-4 ${
            tab === 'mine'
              ? 'border-green-700 text-green-800'
              : 'border-transparent text-gray-500 hover:text-green-700'
          }`}
        >
          أسئلتي
        </button>
        <button
          type="button"
          onClick={() => selectedSubjectId && fetchComments(tab, page, selectedSubjectId)}
          disabled={!selectedSubjectId}
          className="mr-auto flex min-h-11 min-w-11 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-green-50 hover:text-green-700 disabled:opacity-40"
          aria-label="تحديث"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Feed */}
      {!selectedSubjectId && !subjectsLoading && !subjectsError ? (
        <div className={`${cardTheme.shell} p-8 text-center sm:p-10`}>
          <div
            className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${cardTheme.iconBoxGold}`}
          >
            <BookOpen className="h-7 w-7" />
          </div>
          <p className={`mb-1 text-base font-bold ${cardTheme.title}`}>اختر مادة لعرض التعليقات</p>
          <p className={`text-sm ${cardTheme.subtitle}`}>التعليقات تظهر حسب المادة المسجّلة</p>
        </div>
      ) : loading || subjectsLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`${cardTheme.shell} h-28 animate-pulse`} />
          ))}
        </div>
      ) : error ? (
        <div className={`${cardTheme.shell} border-red-200 p-6 text-center text-sm text-red-600`}>
          {error}
        </div>
      ) : comments.length === 0 ? (
        <div className={`${cardTheme.shell} p-8 text-center sm:p-10`}>
          <div
            className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${cardTheme.iconBoxGold}`}
          >
            <MessageSquare className="h-7 w-7" />
          </div>
          <p className={`mb-1 text-base font-bold ${cardTheme.title}`}>
            {tab === 'feed' ? 'لا توجد تعليقات بعد' : 'لم تكتب أي أسئلة بعد'}
          </p>
          <p className={`text-sm ${cardTheme.subtitle}`}>
            {tab === 'feed'
              ? 'كن أول من يشارك سؤالاً في هذه المادة'
              : 'اضغط زر السؤال الجديد بالأسفل للبدء'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map((comment) => (
            <CommentCard key={comment.id} comment={comment} showStatus={tab === 'mine'} />
          ))}
        </div>
      )}

      {totalPages > 1 && !loading && selectedSubjectId && (
        <div className="flex flex-wrap items-center justify-center gap-3 pt-5 pb-16 sm:pb-0">
          <button
            type="button"
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className={`min-h-11 rounded-xl border border-green-200 px-4 text-sm disabled:opacity-40 ${cardTheme.btnOutline}`}
          >
            السابق
          </button>
          <span className="text-sm text-gray-500">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            className={`min-h-11 rounded-xl border border-green-200 px-4 text-sm disabled:opacity-40 ${cardTheme.btnOutline}`}
          >
            التالي
          </button>
        </div>
      )}

      {/* Floating add-question — bottom-right so it doesn’t overlap SupportTelegramFab (bottom-left) */}
      <button
        type="button"
        onClick={openCompose}
        disabled={subjects.length === 0}
        className={`fixed bottom-5 right-4 z-40 flex min-h-14 items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold shadow-lg shadow-green-900/30 transition hover:scale-[1.03] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 sm:bottom-6 sm:right-6 sm:px-5 ${cardTheme.btnGold}`}
        aria-label="إضافة سؤال جديد"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-green-950/10">
          <Plus className="h-5 w-5" />
        </span>
        <span className="hidden min-[380px]:inline">سؤال جديد</span>
      </button>
    </div>
  );
};

export default PortalForum;
